import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEnter } from "@/lib/motion";
import { Mascot } from "@/components/Mascot";
import { usePageMeta, NOT_FOUND_META } from "@/lib/pageMeta";

// A studio's 404 is free canvas: nobody arrives here on purpose, so nothing is
// lost by making it the one page with a joke in it. It is also the page most
// likely to get screenshotted by the kind of person who notices such things,
// which for a branding studio is the audience.
//
// The mascot rather than a large "404": the number is what every error page
// reaches for, and this site has a character sitting unused on one route. Same
// component as the studio page, so it leans toward the pointer here too — the
// fox watching you be lost is the whole gag.
//
// COPY PROVISÓRIA. The voice is the studio's to set; André has not approved
// these two lines. They are written to be replaceable without touching layout.
const COPY = {
    heading: "Não tem nada aqui.",
    body: "O endereço que você abriu não existe no site. O mascote, por outro lado, existe — e está achando graça.",
    action: "VOLTAR PARA A HOME",
    aside: "ou fale com o estúdio",
};

export const NotFound = () => {
    // No path, so no canonical: a 404 has no address worth pointing search
    // engines at, and the hook clears any the previous route left behind.
    usePageMeta(NOT_FOUND_META);

    // useEnter, not useReveal: this page is short enough that its content never
    // crosses a scroll trigger, so a whileInView reveal would leave it invisible.
    const enter = useEnter();

    return (
        <section className="mx-auto grid min-h-[70vh] max-w-[1340px] items-center gap-10 px-6 py-24 md:px-12 lg:grid-cols-[1fr_auto] lg:gap-16">
            <div className="order-2 lg:order-1">
                <motion.h1
                    className="type-display max-w-[560px] text-balance text-[clamp(34px,5vw,64px)] leading-[1.05] text-brand"
                    {...enter()}
                >
                    {COPY.heading}
                </motion.h1>

                <motion.p
                    className="mt-5 max-w-[480px] text-[clamp(16px,1.7vw,20px)] leading-relaxed text-ink/70"
                    {...enter(0.08)}
                >
                    {COPY.body}
                </motion.p>

                <motion.div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4" {...enter(0.16)}>
                    <Link
                        to="/"
                        className="type-display inline-flex h-[50px] w-[220px] items-center justify-center bg-brand text-action text-paper btn-sweep [--sweep:theme(colors.brand.deep)]"
                    >
                        {COPY.action}
                    </Link>
                    <Link
                        to="/contato"
                        className="link-sweep text-[15px] font-medium text-ink/70"
                    >
                        {COPY.aside}
                    </Link>
                </motion.div>
            </div>

            {/* Sized in vw so it stays a companion to the type rather than
                overtaking it, and capped so it does not balloon on a wide screen. */}
            <motion.div
                className="order-1 w-[min(60vw,300px)] justify-self-center lg:order-2 lg:w-[min(32vw,420px)]"
                {...enter(0.1)}
            >
                <Mascot className="w-full" />
            </motion.div>
        </section>
    );
};
